import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button, CloseButton, Form, Table } from 'react-bootstrap';
import * as XLSX from 'xlsx-js-style';
import axios from 'axios';
import { getSessionStorage } from 'assets/constant';

const WEEKS = Array.from({ length: 15 }, (_, i) => i + 1);
const SESSIONS = [1, 2, 3];

const ExcelAttendanceUploadPopup = ({ onClose, openYy, openTmGbn }) => {
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedSession, setSelectedSession] = useState(1);
  const [sessionTitles, setSessionTitles] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const fileInputRef = useRef(null);

  const fetchSessionTitles = useCallback(async (week, session) => {
    try {
      const token = getSessionStorage();
      const termParams = (openYy && openTmGbn) ? `&openYy=${openYy}&openTmGbn=${openTmGbn}` : '';
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER}/attendance/excel/sessions?week=${week}&session=${session}${termParams}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const titles = res.data?.data ?? res.data ?? [];
      setSessionTitles(titles);
      setSelectedTitle('');
    } catch (err) {
      setSessionTitles([]);
      setSelectedTitle('');
    }
  }, [openYy, openTmGbn]);

  useEffect(() => {
    fetchSessionTitles(selectedWeek, selectedSession);
  }, [selectedWeek, selectedSession, fetchSessionTitles]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setResult(null);
  };

  const handleTemplateDownload = () => {
    const wsData = [['학번', '이름', '학과']];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '출석');
    XLSX.writeFile(wb, '출석_업로드_양식.xlsx');
  };

  const CHUNK_SIZE = 500;

  const handleUpload = async () => {
    if (!file) {
      alert('엑셀 파일을 선택해주세요.');
      return;
    }
    if (sessionTitles.length >= 1 && !selectedTitle) {
      alert('과제를 선택해주세요.');
      return;
    }

    let url = `${process.env.REACT_APP_SERVER}/attendance/excel?week=${selectedWeek}&session=${selectedSession}`;
    if (selectedTitle) {
      url += `&assignmentTitle=${encodeURIComponent(selectedTitle)}`;
    }

    setLoading(true);
    setProgress(null);
    try {
      // 엑셀 파싱
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      const headerRow = rows[0];
      const dataRows = rows.slice(1).filter(row => row.some(cell => cell !== '' && cell != null));

      // 500명씩 청크 분할
      const chunks = [];
      for (let i = 0; i < dataRows.length; i += CHUNK_SIZE) {
        chunks.push(dataRows.slice(i, i + CHUNK_SIZE));
      }

      const token = getSessionStorage();
      let totalSuccess = 0;
      let totalFail = [];

      for (let i = 0; i < chunks.length; i++) {
        setProgress({ current: i + 1, total: chunks.length });

        // 청크를 엑셀 파일로 변환
        const chunkSheet = XLSX.utils.aoa_to_sheet([headerRow, ...chunks[i]]);
        const chunkWb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(chunkWb, chunkSheet, 'Sheet1');
        const chunkBuffer = XLSX.write(chunkWb, { type: 'array', bookType: 'xlsx' });
        const chunkBlob = new Blob([chunkBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const formData = new FormData();
        formData.append('file', new File([chunkBlob], 'chunk.xlsx'));

        const res = await axios.post(url, formData, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const resultData = res.data?.data ?? res.data;
        totalSuccess += resultData?.successCount ?? 0;
        if (resultData?.failList?.length > 0) {
          totalFail = [...totalFail, ...resultData.failList];
        }
      }

      const finalResult = { successCount: totalSuccess, failList: totalFail };
      setResult(finalResult);

      if (totalFail.length > 0) {
        const lines = totalFail.map(item => `${item.loginId}(${item.name}) - ${item.reason}`);
        const text = `출석 업로드 실패 목록\n${selectedWeek}주차 ${selectedSession}차시\n\n${lines.join('\n')}`;
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `출석업로드_실패목록_${selectedWeek}주차${selectedSession}차시.txt`;
        a.click();
        URL.revokeObjectURL(blobUrl);
      }
    } catch (err) {
      alert('업로드 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  return (
    <>
      <div onClick={onClose} className="backdrop w-100 h-100 position-fixed top-0 start-0" style={{ zIndex: 1040 }} />
      <div
        className="bg-white position-fixed position-center p-4 rounded-2 popupContainer w-100"
        style={{ maxWidth: 560, maxHeight: '85vh', overflowY: 'auto', zIndex: 1050 }}
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="fw-bold mb-0">출석 엑셀 업로드</h5>
          <CloseButton onClick={onClose} />
        </div>

        {/* 주차 / 차시 선택 */}
        <div className="d-flex gap-3 mb-3">
          <Form.Group>
            <Form.Label className="fw-semibold">주차</Form.Label>
            <Form.Select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(Number(e.target.value))}
              style={{ width: 120 }}
            >
              {WEEKS.map((w) => (
                <option key={w} value={w}>{w}주차</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label className="fw-semibold">차시</Form.Label>
            <Form.Select
              value={selectedSession}
              onChange={(e) => setSelectedSession(Number(e.target.value))}
              style={{ width: 120 }}
            >
              {SESSIONS.map((s) => (
                <option key={s} value={s}>{s}차시</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        {/* 중복 과제 선택 (2개 이상일 때만 표시) */}
        {sessionTitles.length >= 1 && (
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">과제 선택 <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={selectedTitle}
              onChange={(e) => setSelectedTitle(e.target.value)}
            >
              <option value="">-- 과제를 선택하세요 --</option>
              {sessionTitles.map((title) => (
                <option key={title} value={title}>{title}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">동일 주차/차시에 여러 과제가 존재합니다. 업로드할 과제를 선택하세요.</Form.Text>
          </Form.Group>
        )}

        {/* 양식 다운로드 */}
        <div className="mb-3">
          <Button variant="outline-secondary" size="sm" onClick={handleTemplateDownload}>
            <i className="bi bi-download me-1" />
            양식 다운로드
          </Button>
        </div>

        {/* 파일 선택 */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">엑셀 파일 선택</Form.Label>
          <Form.Control
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
          />
        </Form.Group>

        {/* 업로드 버튼 */}
        <div className="d-flex justify-content-end mb-3">
          <Button variant="primary" onClick={handleUpload} disabled={loading || !file || (sessionTitles.length >= 1 && !selectedTitle)}>
            {loading
              ? progress
                ? `처리 중... (${Math.round((progress.current / progress.total) * 100)}%)`
                : '처리 중...'
              : '업로드'}
          </Button>
        </div>

        {/* 결과 */}
        {result && (
          <div>
            <p className="fw-semibold mb-2">
              처리 결과: <span className="text-success">성공 {result.successCount}명</span>
              {result.failList?.length > 0 && (
                <span className="text-danger ms-2">실패 {result.failList.length}명</span>
              )}
            </p>
            {result.failList?.length > 0 && (
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th>학번</th>
                    <th>이름</th>
                    <th>실패 사유</th>
                  </tr>
                </thead>
                <tbody>
                  {result.failList.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.loginId}</td>
                      <td>{item.name}</td>
                      <td>{item.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ExcelAttendanceUploadPopup;
